import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  CreditCard,
  KeyRound,
  RefreshCw,
  Scale,
  ShieldCheck,
  Trash2,
  WifiOff,
  Activity,
} from "lucide-react";
import { usePageMeta } from "../hooks/usePageMeta";
import { ContactSection } from "../components/sections/ContactSection";

const linkCls = "font-medium text-teal-deep underline-offset-2 hover:underline";

// Self-serve first: every common request has an in-app fix the user can do
// themselves, so nobody has to wait on a reply. The contact form below is the
// fallback for anything else.
const TOPICS: { icon: typeof Scale; title: string; body: ReactNode }[] = [
  {
    icon: KeyRound,
    title: "Signing in",
    body: (
      <>
        FitBuddy signs you in with a six-digit code texted to your mobile. No
        code? Check the country code, wait 30 seconds and tap resend. Changed
        number? Use <strong>Account recovery</strong> on the sign-in screen.
      </>
    ),
  },
  {
    icon: Scale,
    title: "How my calorie bank is worked out",
    body: (
      <>
        Each weigh-in can earn from three streams: a new all-time low, a new
        three-day-average low and time since your last weigh-in. Tap any
        earning in the app to see the exact sum with your own numbers.
      </>
    ),
  },
  {
    icon: CreditCard,
    title: "Shop orders",
    body: (
      <>
        The FitBuddy app is free, with no subscription. If you bought something
        from the shop, you can track it or ask about a refund in the app under{" "}
        <strong>Shop</strong>, then your order.
      </>
    ),
  },
  {
    icon: RefreshCw,
    title: "Fix a wrong entry or start fresh",
    body: (
      <>
        Swipe any weigh-in, food or workout to edit or delete it, including its
        date and time. For a clean slate, Settings has{" "}
        <strong>Reset all my data</strong> (keeps your account) and{" "}
        <strong>Reset cals to 2,000</strong> (keeps your history).
      </>
    ),
  },
  {
    icon: WifiOff,
    title: "Logged offline?",
    body: (
      <>
        Weigh-ins and food logged without signal are saved on your phone and
        sync on their own once you're back online. Keep the app signed in until
        they've synced.
      </>
    ),
  },
  {
    icon: Activity,
    title: "WHOOP connection",
    body: (
      <>
        Connect or disconnect WHOOP from its card in the app at any time. If your activity
        stops updating, disconnect and reconnect to refresh access.
      </>
    ),
  },
  {
    icon: ShieldCheck,
    title: "Your data and privacy",
    body: (
      <>
        What we collect, why, and who processes it is all in the{" "}
        <Link to="/privacy" className={linkCls}>
          Privacy Policy
        </Link>
        . You can export your history as CSV from the charts screen in the app.
      </>
    ),
  },
  {
    icon: Trash2,
    title: "Delete my account",
    body: (
      <>
        Do it in the app (Settings, then Delete account &amp; data) or on the
        web from the{" "}
        <Link to="/delete-account" className={linkCls}>
          account deletion page
        </Link>
        . It's instant and no one needs to approve it.
      </>
    ),
  },
];

export default function Support() {
  usePageMeta({
    title: "FitBuddy Support | Help with the app",
    description:
      "Get help with FitBuddy: signing in, your calorie bank, shop orders, offline sync, WHOOP, privacy and deleting your account. Most fixes take a tap.",
    path: "/support",
  });
  return (
    <>
      <section className="relative pt-32 pb-8">
        <div className="container-x max-w-5xl">
          <h1 className="display text-4xl text-navy sm:text-5xl">FitBuddy support</h1>
          <p className="mt-4 max-w-2xl text-lg leading-relaxed text-charcoal/80">
            Most things can be sorted in a tap inside the app. Find your
            question below, and if it isn't covered, send us a message at the
            bottom of the page.
          </p>

          <div className="mt-12 grid gap-5 sm:grid-cols-2">
            {TOPICS.map(({ icon: Icon, title, body }) => (
              <article key={title} className="card-pop p-6">
                <div className="flex items-center gap-3">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border-[3px] border-navy bg-teal-mist text-teal-deep">
                    <Icon className="h-5 w-5" aria-hidden="true" />
                  </span>
                  <h2 className="font-display text-lg font-bold text-navy">{title}</h2>
                </div>
                <p className="mt-4 leading-relaxed text-charcoal/80">{body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <ContactSection
        eyebrow="Still stuck?"
        title={
          <>
            Send us a <span className="marker-teal marker">message</span>
          </>
        }
        sub="Tell us what's going on and the email to reply to. Include the phone model and app version if something isn't working."
      />
    </>
  );
}
